import * as THREE from 'three';
import { ORDER } from '../core/systems.js';
import { CFG } from '../core/config.js';
import { buildSpriteAtlas, buildTrailTexture, buildDecalAtlas, SPR } from './atlas.js';
import { ParticlePool, P, resetP } from './particles.js';
import { TrailSet } from './trails.js';
import { GroundLayer } from './ground.js';
import { BeamPool, B, resetB } from './beams.js';
import { DEFS, ELEMENTS, lin } from './defs.js';

/**
 * ###########################################################################
 * #                                                                         #
 * #   world.vfx — the visual effects API                                    #
 * #                                                                         #
 * ###########################################################################
 *
 * Every call is safe to make at any time, from any system, at any rate. All
 * of them are allocation-free, all of them clamp themselves against a
 * per-frame particle budget, and none of them will ever throw if the terrain,
 * camera or pipeline is not ready yet.
 *
 * ---------------------------------------------------------------------------
 * CONTRACT §5 — the calls other modules already depend on
 * ---------------------------------------------------------------------------
 *
 *  vfx.slash(x,y,z, dirX,dirY,dirZ, opts)
 *      A swept crescent card facing `dir`, i.e. one attack arc. Use this for
 *      every unit that has no per-bone weapon transform (the whole crowd).
 *      opts: { power=1, scale=1, element='none', roll=<radians>, life }
 *
 *  vfx.spark(x,y,z, dirX,dirY,dirZ, power=1, opts)
 *      The contact spark. `dir` is the direction the blow travels; the streak
 *      fan is emitted in the plane facing it, which is what makes a hit read
 *      as a hit and not as a firework.
 *      opts: { kind='slash'|'blunt'|'crit'|'block'|'pierce', element, scale }
 *
 *  vfx.dust(x,y,z, power=1, opts)
 *      Ground puff at a contact point. opts: { kind='step'|'land'|'slide',
 *      dirX, dirZ, color }  — colour defaults to the terrain material there.
 *
 *  vfx.ring(x,y,z, opts)
 *      Terrain-conformed expanding shockwave ring.
 *      opts: { radius=4, life=0.4, thick=0.1, color, alpha=1, element }
 *
 *  vfx.burst(x,y,z, opts)
 *      Omnidirectional blast. opts: { power=1, element, kind='burst'|'musou'|
 *      'finish', flash=true }
 *
 *  vfx.decal(x,y,z, opts)
 *      Project a mark onto the terrain from a fixed, oldest-first pool.
 *      opts: { kind='scorch'|'scuff'|'boot'|'crater'|'crack'|'splat'|'drag'|
 *      'ash'|'dirt', radius=1, yaw=0, life=25, alpha=1, color }
 *
 *  vfx.trail(handleOrOpts)
 *      With no argument or an options object: acquire a ribbon and return its
 *      handle. With an existing handle: keep it alive and return it.
 *      Handle API: push(tipX,tipY,tipZ, rootX,rootY,rootZ) | follow(obj3d,
 *      tipY, rootY) | configure(opts) | stop() | kill() | .alive
 *      opts: { element, width=1, intensity=1, fadeFrames=10, taper=0.55,
 *              obj, tipY, rootY, hot:[r,g,b], tail:[r,g,b] }
 *
 * ---------------------------------------------------------------------------
 * EXTENDED API
 * ---------------------------------------------------------------------------
 *
 *  vfx.emit(defName, x,y,z, opts)      spawn any effect in the DEFS table.
 *      opts: { power, scale, element, dirX,dirY,dirZ, color, color2, life }
 *      The table lives in `src/vfx/defs.js` and is pure data — add an entry
 *      there and it is immediately callable. `vfx.defs` is that table.
 *
 *  vfx.aura(idOrNull, opts)            full-body aura (musou / element).
 *      opts: { element='none', seconds=3, intensity=1, x,y,z (when id<0),
 *              radius=0.55, shafts=true, ground=true }
 *      Returns a handle: { stop(), refresh(seconds), set(opts) }.
 *
 *  vfx.beam(x0,y0,z0, x1,y1,z1, opts)  stretched quad between two points.
 *      opts: { style=0 shaft |1 bolt |2 pillar |3 spike, width=0.25, width2,
 *              life=0.3, color, color2, bright=1, jitter=0.2, element }
 *
 *  vfx.footstep(x,y,z, power=1)        one footfall puff (terrain-tinted)
 *  vfx.land(x,y,z, power)              landing impact: ring + dust + decal
 *  vfx.slide(x,y,z, dirX,dirZ, power)  dirt kicked up by a knockback slide
 *  vfx.impactDust(x,y,z, power)        body-hits-ground puff
 *  vfx.element(name)                   the element record (colours, sprites)
 *  vfx.light(x,y,z, color, intensity, radius, life)   pooled point-light flash
 *  vfx.haze(on)                        toggle the persistent battlefield haze
 *  vfx.weaponTrail(on, opts)           hero blade ribbon on/off. Builds TWO
 *      ribbons on the same blade — a narrow near-white core and a wider,
 *      dimmer glow that reaches past the point — for one draw call.
 *      opts: { element, intensity=1, width=1, fadeFrames=8, hold=0.30 }
 *  vfx.clear()                         wipe everything on screen
 *  vfx.setQuality()                    re-read CFG.q (bus 'quality' does this)
 *  vfx.setSceneDepth(tex, near, far)   enable soft particles once the render
 *                                      pipeline exposes its depth attachment
 *  vfx.stats                           { particles, trails, beams, decals,
 *                                        drawCalls, spawnedThisFrame }
 *  vfx.budget                          { particles, perFrame, decals }
 *  vfx.group                           the THREE.Group holding every VFX mesh
 *  vfx.debug                           capture-only helpers (see below)
 *
 * ---------------------------------------------------------------------------
 * AUTOMATIC HOOKS
 * ---------------------------------------------------------------------------
 * VFX listens on the bus so most of this happens without anyone calling it:
 *   'anim:event' {type:'foot'}    -> footstep dust at the planted foot, at the
 *                                    right foot, terrain-tinted, distance-gated
 *   'anim:event' {type:'swoosh'}  -> hero blade ribbon on (auto-off after
 *                                    0.30 s) / a slash card for crowd units,
 *                                    rate-capped at 6 per frame
 *   'anim:event' {type:'vfx'}     -> emit(marker.name) at the unit
 *   'quality'                     -> re-reads CFG.q
 *
 * The `'hit'` bus event is deliberately NOT subscribed: `world.impact.hit()`
 * already routes to `vfx.spark()`, and listening as well would double every
 * spark in the game. Set `world.vfxAutoHit = true` before VFX init if you want
 * the fallback.
 *
 * ---------------------------------------------------------------------------
 * COST
 * ---------------------------------------------------------------------------
 * SIX meshes, and never more, whatever is on screen:
 *   1 additive particles   2 alpha particles   3 weapon ribbons
 *   4 beams                5 ground decals     6 ground additive (shockwaves)
 * Each hides itself when its pool is empty, so an idle frame costs 1-2 draw
 * calls and a full musou costs 6. Measured A/B (`__DW.scenario('vfxCounts')`
 * and the `vfx_cost` capture) puts the whole system at 7 draw calls at full
 * load against a 14 budget.
 *
 * Plus three pooled PointLights, added once at init and never removed so
 * NUM_POINT_LIGHTS never changes and nothing recompiles mid-fight. They cost
 * no draw calls; they add one light slot to already-lit materials.
 *
 * Nothing here allocates after init. Emission fills the module-scope scratch
 * records `P` / `B` instead of building option objects.
 * ###########################################################################
 */

/* ---------------------------------------------------------------- scratch -- */
const _v = new THREE.Vector3();
const _cam = new THREE.Vector3();
const _col = new THREE.Color();
let _dx = 0, _dy = 1, _dz = 0;          // sampled emission direction
let _ux = 1, _uy = 0, _uz = 0;          // basis perpendicular to the effect dir
let _vx = 0, _vy = 0, _vz = 1;

/** Terrain material id -> dust colour. 0 grass 1 dirt 2 road 3 stone 4 sand */
const DUST_COL = [0x9c9478, 0xa08a6c, 0xb0a289, 0x9a978f, 0xcbb692];

const DECAL_CELL = {
  scorch: 0, scorchSoft: 1, scuff: 2, scuffLong: 3, bootL: 4, bootR: 5,
  crater: 6, crack: 7, splat: 8, drag: 9, ash: 10, dirt: 11,
  ringHard: 12, ringSoft: 13, glow: 14, rune: 15,
};

/**
 * Blade tip / root in weapon-local metres plus authored trail-width scalar.
 *
 * EVERY weapon in `MODELS` needs a row. The lookup below falls back to the
 * glaive when a name is missing, and that fallback is silent: the gada shipped
 * in Step 8 wearing a glaive's trail, tip at 1.90 m on a mace whose geometry
 * ends at 1.22 — 0.68 m of ribbon drawn in empty air past the end of the
 * weapon — and the cambuk did the same. Nothing failed, nothing warned, and
 * the gada's own critic capture did not catch it. `weapon-distinct.mjs` now
 * asserts both the coverage and the overshoot.
 *
 * The tip should sit a little PAST the geometry (the trail reaches slightly
 * beyond the point of the blade on purpose — see trails.js); the shipped band
 * is 0.03-0.20 m. The root is where the business end starts, not the pommel.
 */
const BLADE = {
  greatGlaive: [1.90, 0.98, 0.34], spear: [1.46, 1.02, 0.27], dao: [1.05, 0.26, 0.42],
  halberd: [1.62, 0.94, 0.31],
  /* was [0.95, 0.30, 0.44]: 0.41 m past the head of a weapon 0.70 m long, so
   * more than half the ribbon hung off the end of the axe. */
  axe: [0.62, 0.20, 0.40],
  banner: [1.6, 0.9, 0.30],
  bow: [0.9, -0.9, 0.18],
  /* The gada's business end IS its head — a short, fat arc, not a blade. */
  gada: [1.30, 0.72, 0.30],
  /* The cambuk's is the naga head at the end of the chain, and the ribbon is
   * narrow because the thing sweeping is 0.06 m of iron, not a glaive's edge.
   * The root sits up at the first link so the trail draws the CHAIN's arc
   * rather than a wedge back to the fist. */
  cambuk: [2.14, 1.05, 0.19],
  /* Short weapon, short ribbon: the bit is the only part that cuts and it ends
   * at 0.56 m. A wide trail on a 0.88 m axe would read as a sword's arc. */
  kapak: [0.66, 0.24, 0.33],
};

export function createVFX() {
  /* ------------------------------------------------------------- state --- */
  let W = null;
  let rng = null;
  let now = 0;                 // the VFX clock: advanced with realDt
  let group = null;
  let atlasTex = null, trailTex = null, decalTex = null;
  let padd = null, palpha = null, trails = null, beams = null;
  let gAlpha = null, gAdd = null;
  let lights = [];
  let api = null;

  let budget = { particles: 16000, perFrame: 1200, reserve: 216,
                 beamsPerFrame: 48, beamReserve: 10, decals: 384 };
  let density = 1;             // quality scale on particle counts
  let hazeOn = true, hazeAcc = 0, hazeCount = 0;
  let heroTrail = null, heroGlow = null, heroTrailUntil = 0, heroWeapon = null;
  let crowdSlashBudget = 0;

  const auras = [];            // pooled aura records
  const sweeps = [];           // debug-driven synthetic blade sweeps

  /** Fixed records: capture code can diff exact event ownership without maps. */
  const telemetry = {
    requested: 0, spawned: 0, dropped: 0, overwrites: 0,
    particleRequested: 0, particleSpawned: 0, particleDropped: 0,
    beamRequested: 0, beamSpawned: 0, beamDropped: 0,
    primary: 0, fill: 0, semantic: 0, markers: 0,
    releases: 0, musouHits: 0, finishes: 0,
    rings: 0, impulses: 0, flashes: 0,
  };
  const frameEmission = {
    frame: -1, requested: 0, spawned: 0, dropped: 0, overwrites: 0,
    particleRequested: 0, particleSpawned: 0, particleDropped: 0,
    beamRequested: 0, beamSpawned: 0, beamDropped: 0,
    particleSubmitted: 0, beamSubmitted: 0,
  };
  const stats = {
    particles: 0, addLive: 0, alphaLive: 0, addInstances: 0, alphaInstances: 0,
    trails: 0, beams: 0, beamInstances: 0, decals: 0, drawCalls: 6, spawnedThisFrame: 0,
    auras: 0, haze: 0, telemetry, frameEmission,
  };

  function beginEmissionFrame() {
    const f = W?.time?.frame ?? 0;
    if (frameEmission.frame === f) return;
    frameEmission.frame = f;
    frameEmission.requested = frameEmission.spawned = frameEmission.dropped = 0;
    frameEmission.overwrites = 0;
    frameEmission.particleRequested = frameEmission.particleSpawned = frameEmission.particleDropped = 0;
    frameEmission.beamRequested = frameEmission.beamSpawned = frameEmission.beamDropped = 0;
    frameEmission.particleSubmitted = frameEmission.beamSubmitted = 0;
  }

  function resetTelemetry() {
    telemetry.requested = telemetry.spawned = telemetry.dropped = telemetry.overwrites = 0;
    telemetry.particleRequested = telemetry.particleSpawned = telemetry.particleDropped = 0;
    telemetry.beamRequested = telemetry.beamSpawned = telemetry.beamDropped = 0;
    telemetry.primary = telemetry.fill = telemetry.semantic = telemetry.markers = 0;
    telemetry.releases = telemetry.musouHits = telemetry.finishes = 0;
    telemetry.rings = telemetry.impulses = telemetry.flashes = 0;
    frameEmission.frame = -1;
    beginEmissionFrame();
  }

  function grant(requested, priority, beam = false) {
    beginEmissionFrame();
    const req = Math.max(0, requested | 0);
    const pri = priority > 2 ? 2 : priority < 0 ? 0 : priority | 0;
    const cap = beam ? budget.beamsPerFrame : budget.perFrame;
    const reserve = beam ? budget.beamReserve : budget.reserve;
    const used = beam ? frameEmission.beamSubmitted : frameEmission.particleSubmitted;
    const limit = pri >= 2 ? cap : pri === 1 ? cap - Math.ceil(reserve * 0.5) : cap - reserve;
    const n = Math.max(0, Math.min(req, limit - used));
    const miss = req - n;
    telemetry.requested += req; frameEmission.requested += req;
    if (beam) {
      telemetry.beamRequested += req; frameEmission.beamRequested += req;
      telemetry.beamDropped += miss; frameEmission.beamDropped += miss;
      frameEmission.beamSubmitted += n;
    } else {
      telemetry.particleRequested += req; frameEmission.particleRequested += req;
      telemetry.particleDropped += miss; frameEmission.particleDropped += miss;
      frameEmission.particleSubmitted += n;
    }
    telemetry.dropped += miss; frameEmission.dropped += miss;
    return n;
  }

  function pushed(pool, slot, beforeOver, beam = false) {
    const over = pool.overwrites - beforeOver;
    if (over > 0) {
      telemetry.overwrites += over; frameEmission.overwrites += over;
    }
    if (slot < 0) {
      telemetry.dropped++; frameEmission.dropped++;
      if (beam) { telemetry.beamDropped++; frameEmission.beamDropped++; }
      else { telemetry.particleDropped++; frameEmission.particleDropped++; }
      return false;
    }
    telemetry.spawned++; frameEmission.spawned++;
    if (beam) { telemetry.beamSpawned++; frameEmission.beamSpawned++; }
    else { telemetry.particleSpawned++; frameEmission.particleSpawned++; }
    return true;
  }

  /* ------------------------------------------------------------- random --- */
  const rnd = () => rng.next();
  const rr = (a, b) => a + (b - a) * rng.next();
  /** Read a def field that may be a scalar, a [min,max] pair, or missing. */
  const num = (v, d) => (v === undefined ? d : (Array.isArray(v) ? rr(v[0], v[1]) : v));
  const pick = (v, d) => (v === undefined ? d : (Array.isArray(v) ? v[(rng.next() * v.length) | 0] : v));

  /* --------------------------------------------------------- directions --- */
  function basisFrom(dx, dy, dz) {
    const l = Math.hypot(dx, dy, dz);
    if (l < 1e-5) { dx = 0; dy = 1; dz = 0; } else { dx /= l; dy /= l; dz /= l; }
    const hx = Math.abs(dy) < 0.92 ? 0 : 1, hy = Math.abs(dy) < 0.92 ? 1 : 0;
    // u = normalize(cross(d, h))
    let ux = dy * 0 - dz * hy, uy = dz * hx - dx * 0, uz = dx * hy - dy * hx;
    const ul = Math.hypot(ux, uy, uz) || 1;
    _ux = ux / ul; _uy = uy / ul; _uz = uz / ul;
    // v = cross(d, u)
    _vx = dy * _uz - dz * _uy; _vy = dz * _ux - dx * _uz; _vz = dx * _uy - dy * _ux;
    return l;
  }

  function sampleDir(mode, spread, dx, dy, dz) {
    let a, s, c;
    switch (mode) {
      case 'along': _dx = dx; _dy = dy; _dz = dz; return;
      case 'disc':
        a = rnd() * Math.PI * 2; c = Math.cos(a); s = Math.sin(a);
        _dx = _ux * c + _vx * s; _dy = _uy * c + _vy * s; _dz = _uz * c + _vz * s;
        return;
      case 'ground':
        a = rnd() * Math.PI * 2;
        _dx = Math.cos(a); _dy = rnd() * 0.30; _dz = Math.sin(a);
        return;
      case 'up': {
        const t = rnd() * (spread ?? 0.6);
        a = rnd() * Math.PI * 2; s = Math.sin(t);
        _dx = Math.cos(a) * s; _dy = Math.cos(t); _dz = Math.sin(a) * s;
        return;
      }
      case 'cone': case 'back': {
        const bx = mode === 'back' ? -dx : dx, by = mode === 'back' ? -dy : dy, bz = mode === 'back' ? -dz : dz;
        const t = rnd() * (spread ?? 0.5);
        a = rnd() * Math.PI * 2; s = Math.sin(t); c = Math.cos(t);
        const px = _ux * Math.cos(a) + _vx * Math.sin(a);
        const py = _uy * Math.cos(a) + _vy * Math.sin(a);
        const pz = _uz * Math.cos(a) + _vz * Math.sin(a);
        _dx = bx * c + px * s; _dy = by * c + py * s; _dz = bz * c + pz * s;
        return;
      }
      case 'hemi': {
        const z = rnd(), t = Math.sqrt(1 - z * z);
        a = rnd() * Math.PI * 2;
        const px = _ux * Math.cos(a) + _vx * Math.sin(a);
        const py = _uy * Math.cos(a) + _vy * Math.sin(a);
        const pz = _uz * Math.cos(a) + _vz * Math.sin(a);
        _dx = dx * z + px * t; _dy = dy * z + py * t; _dz = dz * z + pz * t;
        return;
      }
      default: {              // sphere
        const z = rnd() * 2 - 1, t = Math.sqrt(Math.max(0, 1 - z * z));
        a = rnd() * Math.PI * 2;
        _dx = Math.cos(a) * t; _dy = z; _dz = Math.sin(a) * t;
      }
    }
  }

  /* ----------------------------------------------------------- utilities -- */
  function camDist2(x, y, z) {
    if (!W?.camera) return 0;
    W.camera.getWorldPosition(_cam);
    const dx = x - _cam.x, dy = y - _cam.y, dz = z - _cam.z;
    return dx * dx + dy * dy + dz * dz;
  }
  function groundY(x, z) { return W?.terrain ? W.terrain.heightAt(x, z) : 0; }
  function dustColorAt(x, z) {
    const m = W?.terrain ? W.terrain.materialAt(x, z) : 1;
    return DUST_COL[m] ?? DUST_COL[1];
  }
  function elem(name) { return ELEMENTS[name] || ELEMENTS.none; }

  const _ca = new Float32Array(3), _cb = new Float32Array(3);
  /** Resolve a colour field to linear RGB in a reusable buffer. */
  function setCol0(hex, el, which) {
    const out = which === 1 ? _ca : _cb;
    if (hex !== undefined && hex !== null) {
      _col.setHex(hex).convertSRGBToLinear();
      out[0] = _col.r; out[1] = _col.g; out[2] = _col.b;
    } else {
      const src = which === 1 ? el.core : el.tail;
      out[0] = src[0]; out[1] = src[1]; out[2] = src[2];
    }
    return out;
  }

  /* ----------------------------------------------------- point-light pool -- */
  function claimLight(x, y, z, colorHex, intensity, radius, life) {
    let best = null, bestScore = Infinity;
    for (let i = 0; i < lights.length; i++) {
      const L = lights[i];
      const rem = L.until - now;
      const score = rem <= 0 ? -1 : rem * L.i0;
      if (score < bestScore) { bestScore = score; best = L; }
    }
    if (!best) return null;
    if (bestScore > intensity * life * 1.6) return null;   // a brighter light owns it
    best.light.position.set(x, y, z);
    best.light.color.setHex(colorHex);
    best.light.distance = radius;
    best.i0 = intensity; best.born = now; best.until = now + life; best.life = life;
    return best;
  }
  function updateLights() {
    for (let i = 0; i < lights.length; i++) {
      const L = lights[i];
      if (now >= L.until) { L.light.intensity = 0; continue; }
      const t = (now - L.born) / Math.max(1e-4, L.life);
      // hard attack, quadratic decay: reads as a flash, not a lamp
      const a = t < 0.12 ? t / 0.12 : 1;
      const d = (1 - t) * (1 - t);
      L.light.intensity = L.i0 * a * d;
    }
  }

  /* -------------------------------------------------------- layer runner -- */
  /**
   * Run one def. `dx,dy,dz` is the effect direction (already normalised by
   * basisFrom). `o` is the caller's opts object; every field is optional.
   */
  function runLayers(def, x, y, z, dx, dy, dz, o) {
    const layers = def.layers;
    const power = Math.max(0, o.power ?? 1);
    const scale = o.scale ?? 1;
    const priority = o.priority ?? def.priority ?? 1;
    const el = elem(o.element || def.element || 'none');
    const far = camDist2(x, y, z);
    // Everything past 90 m loses its small detail; past 200 m it is a rumour.
    const lod = far > 40000 ? 0.25 : far > 8100 ? 0.55 : 1;
    for (let i = 0; i < layers.length; i++) {
      const L = layers[i];
      switch (L.k) {
        case 'p': runParticles(L, x, y, z, dx, dy, dz, o, el, power, scale, lod, priority); break;
        case 'ring': runRing(L, x, y, z, o, el, power, scale, priority); break;
        case 'glow': runGlow(L, x, y, z, o, el, power, scale); break;
        case 'decal': runDecal(L, x, z, o, power, scale); break;
        case 'beam': runBeam(L, x, y, z, dx, dy, dz, o, el, power, scale, lod, priority); break;
        case 'light': {
          if (lod < 0.5) break;
          claimLight(x, y + (L.yoff ?? 0.4), z, L.color ?? el.light,
            (L.intensity ?? 6) * power, (L.radius ?? 8) * scale, L.life ?? 0.15);
          break;
        }
        case 'impulse':
          telemetry.impulses++;
          W?.pipeline?.impulse?.(L.kind || 'hit', (L.amount ?? 1) * power);
          break;
        case 'shake': W?.camrig?.shake?.((L.amp ?? 0.4) * power, L.freq ?? 24, L.dur ?? 0.2); break;
        default: break;
      }
    }
  }

  function runParticles(L, x, y, z, dx, dy, dz, o, el, power, scale, lod, priority) {
    const pool = L.pool === 'alpha' ? palpha : padd;
    const raw = num(L.n, 1);
    let n = Math.round(raw * power * density * lod);
    if (n < 1 && raw * power * density * lod >= 0.5) n = 1;
    n = grant(n, priority, false);
    if (n <= 0) return;

    const c0 = setCol0(o.color ?? L.col, el, 1);
    const c1 = setCol0(o.color2 ?? L.col2, el, 2);
    const align = L.align ?? 0;
    const mode = L.dir || 'sphere';
    const spread = L.spread;
    const pRoot = Math.sqrt(Math.min(2.25, power));
    const sizeMul = scale * pRoot;
    const speedMul = pRoot;
    const alphaMul = Math.min(1, power);
    const brightMul = Math.min(1.65, power);

    for (let i = 0; i < n; i++) {
      resetP();
      sampleDir(mode, spread, dx, dy, dz);
      const sp = num(L.speed, 0) * speedMul;
      const off = num(L.offset, 0);
      P.x = x + (off ? (rnd() - 0.5) * 2 * off : 0);
      P.y = y + num(L.yoff, 0) + (off ? (rnd() - 0.5) * off : 0);
      P.z = z + (off ? (rnd() - 0.5) * 2 * off : 0);
      P.vx = _dx * sp; P.vy = _dy * sp; P.vz = _dz * sp;
      P.life = num(L.life, 0.5);
      P.size0 = num(L.size0, 0.3) * sizeMul;
      P.size1 = num(L.size1, P.size0) * sizeMul;
      P.sprite = pick(L.sprite, SPR.DOT);
      P.r0 = c0[0]; P.g0 = c0[1]; P.b0 = c0[2];
      P.r1 = c1[0]; P.g1 = c1[1]; P.b1 = c1[2];
      P.a0 = num(L.alpha, 1) * alphaMul; P.a1 = num(L.alpha2, 0) * alphaMul;
      P.bright = num(L.bright, 1) * brightMul;
      P.drag = num(L.drag, 2.5);
      P.grav = num(L.grav, 0);
      P.spin = num(L.spin, 0) + (o.roll ?? 0);
      P.spinRate = num(L.spinRate, 0);
      P.stretch = num(L.stretch, 0);
      P.turb = num(L.turb, 0);
      P.align = align;
      P.fadeIn = num(L.fadeIn, 0.05);
      P.curve = num(L.curve, 1);
      P.delay = num(L.delay, 0);
      const before = pool.overwrites;
      pushed(pool, pool.push(now, priority), before, false);
    }
  }

  function runRing(L, x, y, z, o, el, power, scale, priority) {
    beginEmissionFrame();
    if (!gAdd || power <= 0) { telemetry.dropped++; frameEmission.dropped++; return; }
    const c = L.col ?? o.color ?? el.light;
    _col.setHex(c).convertSRGBToLinear();
    const pRoot = Math.sqrt(Math.min(2.25, power));
    gAdd.stamp(W?.terrain, x, z, num(L.radius, 3) * scale * pRoot,
      rnd() * 6.283, now, {
        life: num(L.life, 0.4), fadeIn: 0.02, mode: 1,
        thick: num(L.thick, 0.10), r: _col.r, g: _col.g, b: _col.b,
        a: num(L.alpha, 1) * (o.alpha ?? 1) * Math.min(1, power), lift: 0.075,
      });
    telemetry.rings++;
  }

  function runGlow(L, x, y, z, o, el, power, scale) {
    if (!gAdd) return;
    _col.setHex(L.col ?? el.light).convertSRGBToLinear();
    if (power <= 0) return;
    gAdd.stamp(W?.terrain, x, z, num(L.radius, 2) * scale * Math.sqrt(Math.min(2.25, power)), rnd() * 6.283, now, {
      life: num(L.life, 0.5), fadeIn: 0.12, mode: 2, spin: rnd() * 6.283,
      r: _col.r, g: _col.g, b: _col.b, a: num(L.alpha, 0.6) * power, lift: 0.06,
    });
  }

  function runDecal(L, x, z, o, power, scale) {
    if (!gAlpha) return;
    _col.setHex(L.col ?? 0x4a3d30).convertSRGBToLinear();
    gAlpha.stamp(W?.terrain, x, z, num(L.radius, 1) * scale, rnd() * 6.283, now, {
      life: num(L.life, 20), fadeIn: num(L.fadeIn, 0.005), mode: 0,
      cell: L.cell ?? 0, r: _col.r, g: _col.g, b: _col.b,
      a: num(L.alpha, 0.5) * power, lift: 0.045,
    });
    stats.decals++;
  }

  function runBeam(L, x, y, z, dx, dy, dz, o, el, power, scale, lod, priority) {
    if (!beams || lod < 0.5) return;
    let n = Math.round(num(L.n, 1) * power * density * lod);
    if (n < 1 && power * density * lod >= 0.5) n = 1;
    n = grant(n, priority, true);
    if (n <= 0) return;
    const pRoot = Math.sqrt(Math.min(2.25, power));
    for (let i = 0; i < n; i++) {
      resetB();
      const len = num(L.len, 3) * scale * pRoot;
      if (L.axis === 'vertical') {
        // A thin authored finisher spine. Its slight attack-direction lean
        // keeps a facing and avoids the generic symmetric light-pillar read.
        const h = Math.hypot(dx, dz) || 1;
        const lean = num(L.lean, 0);
        B.x0 = x; B.y0 = y + num(L.y0, 0); B.z0 = z;
        B.x1 = x + (dx / h) * len * lean;
        B.y1 = B.y0 + len;
        B.z1 = z + (dz / h) * len * lean;
      } else if (L.axis === 'fan') {
        // Fixed, deterministic ordering: three directional cuts share the
        // beam pool and add no temporary objects or draw calls.
        const h = Math.hypot(dx, dz) || 1;
        const fx = dx / h, fz = dz / h;
        const spread = num(L.spread, 0.8);
        const a = n > 1 ? (i / (n - 1) - 0.5) * spread : 0;
        const ca = Math.cos(a), sa = Math.sin(a);
        const rx = fx * ca - fz * sa, rz = fz * ca + fx * sa;
        const rise = num(L.rise, 0.5);
        B.x0 = x - rx * 0.18; B.y0 = y + num(L.y0, 0); B.z0 = z - rz * 0.18;
        B.x1 = x + rx * len; B.y1 = B.y0 + len * rise; B.z1 = z + rz * len;
      } else if (L.up) {
        const a = (i / n) * 6.283 + rnd() * 0.5;
        const tilt = rr(0.25, 0.95);
        B.x0 = x; B.y0 = y + 0.15; B.z0 = z;
        B.x1 = x + Math.cos(a) * len * Math.cos(tilt);
        B.y1 = y + 0.15 + len * Math.sin(tilt);
        B.z1 = z + Math.sin(a) * len * Math.cos(tilt);
      } else {
        sampleDir(L.dir || 'sphere', L.spread, dx, dy, dz);
        B.x0 = x; B.y0 = y; B.z0 = z;
        B.x1 = x + _dx * len; B.y1 = y + _dy * len; B.z1 = z + _dz * len;
      }
      B.life = num(L.life, 0.2);
      B.w0 = num(L.w0, 0.2) * scale * pRoot; B.w1 = num(L.w1, B.w0) * scale * pRoot;
      B.style = L.style ?? 0; B.jitter = num(L.jitter, 0.2);
      B.bright = num(L.bright, 2) * Math.min(1.65, power);
      const c0 = setCol0(L.col, el, 1), c1 = setCol0(L.col2, el, 2);
      B.r0 = c0[0]; B.g0 = c0[1]; B.b0 = c0[2]; B.a0 = num(L.alpha, 1) * Math.min(1, power);
      B.r1 = c1[0]; B.g1 = c1[1]; B.b1 = c1[2]; B.a1 = 0;
      const before = beams.overwrites;
      pushed(beams, beams.push(now, priority), before, true);
    }
  }

  /* ------------------------------------------------------------- auras ---- */
  function auraSlot() {
    for (let i = 0; i < auras.length; i++) if (!auras[i].on) return auras[i];
    let best = auras[0];
    for (let i = 1; i < auras.length; i++) if (auras[i].until < best.until) best = auras[i];
    return best;
  }

  /** A nearby finisher resolves its aura instead of leaving a light pillar. */
  function retireNearbyAuras(x, z, seconds = 0.14) {
    const r2 = 8 * 8;
    for (let i = 0; i < auras.length; i++) {
      const a = auras[i];
      if (!a.on) continue;
      const e = W?.entities;
      const ax = a.id >= 0 && e?.alive[a.id] ? e.x[a.id] : a.x;
      const az = a.id >= 0 && e?.alive[a.id] ? e.z[a.id] : a.z;
      const dx = ax - x, dz = az - z;
      if (dx * dx + dz * dz > r2) continue;
      a.fadeAt = now;
      a.until = Math.min(a.until, now + seconds);
    }
  }

  function stepAura(a, dt) {
    if (!a.on) return;
    if (now >= a.until) { a.on = false; return; }
    let x = a.x, y = a.y, z = a.z;
    if (a.id >= 0 && W.entities.alive[a.id]) {
      const e = W.entities;
      x = e.x[a.id]; y = e.y[a.id]; z = e.z[a.id];
    } else if (a.id >= 0) { a.on = false; return; }
    a.px = x; a.py = y; a.pz = z;
    const el = a.el;
    const fade = now > a.fadeAt
      ? Math.max(0, Math.min(1, (a.until - now) / Math.max(0.001, a.until - a.fadeAt)))
      : 1;
    const inten = a.intensity * fade;
    const h = a.height;

    // rising energy motes
    a.acc += dt * 170 * inten * density;
    let n = a.acc | 0; a.acc -= n;
    if (n > 22) n = 22;
    n = grant(n, a.priority, false);
    for (let i = 0; i < n; i++) {
      resetP();
      const ang = rnd() * 6.283, rad = a.radius * (0.35 + 0.85 * rnd());
      P.x = x + Math.cos(ang) * rad;
      P.y = y + rnd() * h * 0.9;
      P.z = z + Math.sin(ang) * rad;
      P.vx = -Math.cos(ang) * 0.5 + (rnd() - 0.5) * 0.7;
      P.vy = rr(1.6, 4.4);
      P.vz = -Math.sin(ang) * 0.5 + (rnd() - 0.5) * 0.7;
      P.life = rr(0.5, 1.05);
      P.size0 = rr(0.04, 0.09); P.size1 = 0.010;
      P.sprite = el.motes;
      P.r0 = el.hot[0]; P.g0 = el.hot[1]; P.b0 = el.hot[2];
      P.r1 = el.tail[0]; P.g1 = el.tail[1]; P.b1 = el.tail[2];
      P.a0 = 0.68 * fade; P.a1 = 0; P.bright = 2.2 * inten;
      P.drag = 1.1; P.grav = 1.6; P.turb = 0.7; P.align = 1; P.stretch = 0.02;
      const before = padd.overwrites;
      pushed(padd, padd.push(now, a.priority), before, false);
    }

    // the body flame column
    a.acc2 += dt * 55 * inten * density;
    let m = a.acc2 | 0; a.acc2 -= m;
    if (m > 12) m = 12;
    m = grant(m, a.priority, false);
    for (let i = 0; i < m; i++) {
      resetP();
      const ang = rnd() * 6.283, rad = a.radius * (0.62 + 0.55 * rnd());
      P.x = x + Math.cos(ang) * rad;
      P.y = y + rnd() * h * 0.75;
      P.z = z + Math.sin(ang) * rad;
      P.vx = (rnd() - 0.5) * 0.6; P.vy = rr(1.4, 3.2); P.vz = (rnd() - 0.5) * 0.6;
      P.life = rr(0.26, 0.46);
      P.size0 = rr(0.16, 0.32) * a.scale; P.size1 = rr(0.40, 0.74) * a.scale;
      P.sprite = el.body;
      P.r0 = el.hot[0]; P.g0 = el.hot[1]; P.b0 = el.hot[2];
      P.r1 = el.tail[0]; P.g1 = el.tail[1]; P.b1 = el.tail[2];
      P.a0 = 0.24 * fade; P.a1 = 0; P.bright = 1.10 * inten;
      P.drag = 2.0; P.grav = 2.6; P.turb = 0.35; P.fadeIn = 0.14; P.curve = 1.3;
      P.spin = rnd() * 6.283; P.spinRate = (rnd() - 0.5) * 1.4;
      const before = padd.overwrites;
      pushed(padd, padd.push(now, a.priority), before, false);
    }

    // slow radial shafts + a ground pool, both refreshed on a timer
    a.shaft -= dt;
    if (a.shaft <= 0 && a.shafts) {
      a.shaft = 0.26;
      const k = grant(3, a.priority, true);
      for (let i = 0; i < k; i++) {
        resetB();
        const ang = a.spin + (i / k) * 6.283;
        const len = rr(2.4, 3.5) * a.scale;
        B.x0 = x; B.y0 = y + 0.12; B.z0 = z;
        B.x1 = x + Math.cos(ang) * len * 0.42;
        B.y1 = y + 0.12 + len;
        B.z1 = z + Math.sin(ang) * len * 0.42;
        B.life = 0.18; B.w0 = 0.16 * a.scale; B.w1 = 0.035 * a.scale;
        B.style = 3; B.bright = 1.4 * inten;
        B.r0 = el.core[0]; B.g0 = el.core[1]; B.b0 = el.core[2]; B.a0 = 0.30 * fade;
        B.r1 = el.core[0]; B.g1 = el.core[1]; B.b1 = el.core[2]; B.a1 = 0;
        const before = beams.overwrites;
        pushed(beams, beams.push(now, a.priority), before, true);
      }
      a.spin += 0.55;
    }
    a.pool -= dt;
    if (a.pool <= 0 && a.ground) {
      a.pool = 0.28;
      _col.setHex(el.light).convertSRGBToLinear();
      gAdd.stamp(W?.terrain, x, z, 1.35 * a.scale, rnd() * 6.283, now, {
        life: 0.26, fadeIn: 0.16, mode: 2, spin: rnd() * 6.283,
        r: _col.r, g: _col.g, b: _col.b, a: 0.20 * inten, lift: 0.06,
      });
    }
    a.lit -= dt;
    if (a.lit <= 0) {
      a.lit = 0.10;
      claimLight(x, y + h * 0.5, z, el.light, el.lightI * 0.55 * inten, el.lightR * 0.75, 0.12);
    }
  }

  /* -------------------------------------------------------------- haze ---- */
  function stepHaze(dt) {
    if (!hazeOn || !W?.camera || density <= 0) return;
    const target = Math.round(20 * density);
    const life = 13;
    hazeAcc += dt * (target / life);
    let n = hazeAcc | 0; hazeAcc -= n;
    if (n > 3) n = 3;
    n = grant(n, 0, false);
    W.camera.getWorldPosition(_cam);
    const sun = W.sky?.sunColor;
    for (let i = 0; i < n; i++) {
      resetP();
      const a = rnd() * 6.283, rad = 10 + rnd() * 46;
      const x = _cam.x + Math.cos(a) * rad, z = _cam.z + Math.sin(a) * rad;
      P.x = x; P.z = z;
      P.y = groundY(x, z) + rr(0.35, 2.6);
      P.vx = rr(-0.55, 0.55); P.vy = rr(0.02, 0.16); P.vz = rr(-0.55, 0.55);
      P.life = life;
      P.size0 = rr(6, 13); P.size1 = rr(12, 21);
      P.sprite = rnd() < 0.55 ? SPR.SMOKE : SPR.WISP;
      // The haze is what catches the low sun; tint it with the sky's own key.
      const mx = sun ? Math.max(sun.r, sun.g, sun.b) || 1 : 1;
      const k = 0.42;
      P.r0 = sun ? 0.60 + (sun.r / mx) * k : 0.86;
      P.g0 = sun ? 0.58 + (sun.g / mx) * k : 0.82;
      P.b0 = sun ? 0.56 + (sun.b / mx) * k : 0.78;
      P.r1 = P.r0 * 0.86; P.g1 = P.g0 * 0.88; P.b1 = P.b0 * 0.95;
      P.a0 = 0.028; P.a1 = 0.0;
      P.bright = 1; P.drag = 0.12; P.grav = 0.0; P.turb = 0.10;
      P.fadeIn = 0.22; P.curve = 1.1;
      P.spin = rnd() * 6.283; P.spinRate = (rnd() - 0.5) * 0.10;
      const before = palpha.overwrites;
      if (pushed(palpha, palpha.push(now, 0), before, false)) hazeCount++;
    }
  }

  /* --------------------------------------------------------- hero trail --- */
  function findHeroWeapon() {
    const m = W?.chars?.player;
    const wpn = m?.userData?.weapon;
    if (!wpn) return null;
    const nm = (wpn.name || '').replace('weapon_', '');
    const b = BLADE[nm] || BLADE.greatGlaive;
    wpn.userData.__vfxBlade = b;
    return wpn;
  }

  /**
   * Keep the cached hero-weapon reference honest.
   *
   * chars/builder.js setPlayerWeapon() DROPS the old mesh from its parent and
   * builds a new one, so a reference cached once at boot goes stale the moment
   * the player changes weapon — and progression hands out three of them (ranks
   * 2, 4 and 7). The failure is silent in the worst way: the ribbon is still
   * created and still sampled every frame, but Trail.step() reads the followed
   * object's matrixWorld, and a DETACHED object's world matrix is just its own
   * local socket transform. Every sample lands on the same two points, the
   * ribbon has zero area, and nothing is drawn — no error, no warning, no
   * trail, for the rest of the session.
   *
   * So compare identity rather than null-ness, and throw away the ribbons
   * bound to the old mesh when it changes.
   */
  function syncHeroWeapon() {
    const cur = W?.chars?.player?.userData?.weapon || null;
    if (cur === heroWeapon) return heroWeapon;
    if (heroTrail) { heroTrail.stop(); heroTrail = null; }
    if (heroGlow) { heroGlow.stop(); heroGlow = null; }
    heroWeapon = cur ? findHeroWeapon() : null;
    return heroWeapon;
  }

  function driveHeroTrail(dt) {
    if (heroTrail && !heroTrail.alive) heroTrail = null;
    if (heroGlow && !heroGlow.alive) heroGlow = null;
    if (now > heroTrailUntil) {
      if (heroTrail) { heroTrail.stop(); heroTrail = null; }
      if (heroGlow) { heroGlow.stop(); heroGlow = null; }
    }
    if (!heroTrail && !heroGlow) return;
    syncHeroWeapon();
    // The animator posed the skeleton in ITS lateUpdate (ORDER.ANIM = 70);
    // ours runs at 80, so this is the only place the blade's world matrix is
    // both current and stable for the frame we are about to render.
    if (heroWeapon) heroWeapon.updateWorldMatrix(true, false);
  }

  /* ------------------------------------------------------------ sweeps ---- */
  function stepSweeps(dt) {
    for (let i = sweeps.length - 1; i >= 0; i--) {
      const s = sweeps[i];
      s.t += dt;
      const u = Math.min(1, s.t / s.dur);
      // ease-in-out so the blade accelerates through the middle of the arc
      const e = u * u * (3 - 2 * u);
      const ang = s.a0 + (s.a1 - s.a0) * e;
      const ca = Math.cos(ang), sa = Math.sin(ang);
      // arc swept about the axis (0 = about Y, 1 = about the X/Z tilt)
      const tx = s.cx + (s.ux * ca + s.vx * sa) * s.rTip;
      const ty = s.cy + (s.uy * ca + s.vy * sa) * s.rTip;
      const tz = s.cz + (s.uz * ca + s.vz * sa) * s.rTip;
      const hx = s.cx + (s.ux * ca + s.vx * sa) * s.rHilt;
      const hy = s.cy + (s.uy * ca + s.vy * sa) * s.rHilt;
      const hz = s.cz + (s.uz * ca + s.vz * sa) * s.rHilt;
      if (s.trail && s.trail.alive) s.trail.push(tx, ty, tz, hx, hy, hz);
      if (s.glow && s.glow.alive) s.glow.push(tx, ty, tz, hx, hy, hz);
      if (u >= 1) {
        if (s.trail && s.trail.alive) s.trail.stop();
        if (s.glow && s.glow.alive) s.glow.stop();
        sweeps.splice(i, 1);
      }
    }
  }

  /* ------------------------------------------------------------- events --- */
  function onAnimEvent(id, m) {
    if (!m || !W) return;
    const e = W.entities;
    if (!e.alive[id]) return;
    const x = e.x[id], y = e.y[id], z = e.z[id];
    if (m.type === 'foot') {
      if (camDist2(x, y, z) > 3600) return;              // 60 m
      const s = m.side === 'L' ? -1 : 1;
      const sx = x + Math.cos(e.yaw[id]) * 0.17 * s;
      const sz = z - Math.sin(e.yaw[id]) * 0.17 * s;
      api.footstep(sx, groundY(sx, sz), sz, id === W.playerId ? 1.1 : 0.7);
    } else if (m.type === 'swoosh') {
      const p = m.power ?? 0.6;
      if (id === W.playerId) {
        api.weaponTrail(true, { intensity: 0.64 + 0.38 * p, hold: 0.16 });
      } else {
        if (crowdSlashBudget <= 0) return;
        if (camDist2(x, y, z) > 1600) return;            // 40 m
        crowdSlashBudget--;
        const yaw = e.yaw[id];
        api.slash(x + Math.sin(yaw) * 0.9, y + 1.25, z + Math.cos(yaw) * 0.9,
          Math.sin(yaw), 0, Math.cos(yaw), { power: p, scale: 0.62, roll: rnd() * 1.2 - 0.6 });
      }
    } else if (m.type === 'vfx' && m.name && DEFS[m.name]) {
      // Animation markers own environmental timing; connected combat owns the
      // move semantic. Ground slams deliberately split those layers so the
      // same blade plant cannot stamp two rings on a successful sweep.
      const def = m.name === 'groundSlam' ? DEFS.ground_slam_marker : DEFS[m.name];
      const dx = m.dirX ?? 0, dy = m.dirY ?? 1, dz = m.dirZ ?? 0;
      const l = Math.hypot(dx, dy, dz) || 1;
      const py = m.name === 'groundSlam' ? groundY(x, z) + 0.06 : y + (m.height ?? 1.0);
      telemetry.markers++;
      basisFrom(dx / l, dy / l, dz / l);
      runLayers(def, x, py, z, dx / l, dy / l, dz / l, m);
    }
  }

  function onHit(id, src, amount, opts) {
    if (!W || id < 0) return;
    const e = W.entities;
    if (!e.alive[id]) return;
    const o = opts || null;
    let dx = 0, dy = 0.25, dz = 0;
    if (src >= 0 && e.alive[src]) {
      dx = e.x[id] - e.x[src]; dz = e.z[id] - e.z[src];
      const l = Math.hypot(dx, dz) || 1; dx /= l; dz /= l;
    }
    api.spark(e.x[id], e.y[id] + 1.05, e.z[id], dx, dy, dz,
      Math.min(2.2, (amount || 40) / 55),
      o ? { kind: o.crit ? 'crit' : (o.kind || 'slash'), element: o.element } : undefined);
  }

  /* ================================================================ SYSTEM */
  return {
    name: 'vfx', order: ORDER.VFX,

    init(w) {
      W = w;
      rng = w.rng.fork(0x5f1c7a);
      now = 0;

      const q = CFG.q || {};
      const pMax = Math.max(2000, q.particleMax || 16000);
      budget.particles = pMax;
      budget.perFrame = Math.max(200, Math.round(pMax * 0.075));
      budget.reserve = Math.max(36, Math.round(budget.perFrame * 0.18));
      budget.decals = Math.max(48, q.decals || 384);
      density = Math.min(1.35, Math.max(0.15, pMax / 16000));

      atlasTex = buildSpriteAtlas(256);
      trailTex = buildTrailTexture(128, 64);
      decalTex = buildDecalAtlas(128);

      padd = new ParticlePool(Math.round(pMax * 0.62), {
        additive: true, map: atlasTex, name: 'vfxParticlesAdd', renderOrder: 10,
      });
      palpha = new ParticlePool(Math.round(pMax * 0.38), {
        additive: false, map: atlasTex, name: 'vfxParticlesAlpha', renderOrder: 9,
      });
      trails = new TrailSet(12, 36, trailTex);
      beams = new BeamPool(128);
      gAlpha = new GroundLayer({
        slots: budget.decals, grid: 2, additive: false, map: decalTex,
        name: 'vfxDecals', renderOrder: 2,
      });
      gAdd = new GroundLayer({
        slots: 40, grid: 6, additive: true, map: decalTex,
        name: 'vfxGroundAdd', renderOrder: 3,
      });

      group = new THREE.Group();
      group.name = 'vfx';
      group.matrixAutoUpdate = false;
      group.add(gAlpha.mesh, gAdd.mesh, palpha.mesh, padd.mesh, beams.mesh, trails.mesh);
      w.scene.add(group);

      // Three pooled point lights, added ONCE at init and never removed, so
      // NUM_POINT_LIGHTS is constant and nothing recompiles mid-fight.
      for (let i = 0; i < 3; i++) {
        const L = new THREE.PointLight(0xffffff, 0, 10, 2);
        L.name = 'vfxLight' + i;
        L.castShadow = false;
        L.visible = true;
        w.scene.add(L);
        lights.push({ light: L, i0: 0, born: -1, until: -1, life: 0.1 });
      }

      for (let i = 0; i < 6; i++) {
        auras.push({
          on: false, id: -1, x: 0, y: 0, z: 0, px: 0, py: 0, pz: 0,
          el: ELEMENTS.none, intensity: 1, until: 0, radius: 0.55, height: 1.9,
          scale: 1, priority: 0, acc: 0, acc2: 0, shaft: 0, pool: 0, lit: 0, spin: 0,
          fadeAt: 0,
          shafts: true, ground: true,
          stop() { this.on = false; return this; },
          refresh(s = 3) {
            this.until = now + s;
            this.fadeAt = this.until - Math.min(0.45, s * 0.30);
            return this;
          },
          set(o = {}) {
            if (o.element) this.el = elem(o.element);
            if (o.intensity !== undefined) this.intensity = o.intensity;
            if (o.radius !== undefined) this.radius = o.radius;
            if (o.height !== undefined) this.height = o.height;
            if (o.scale !== undefined) this.scale = o.scale;
            if (o.priority !== undefined) this.priority = o.priority;
            if (o.shafts !== undefined) this.shafts = !!o.shafts;
            if (o.ground !== undefined) this.ground = !!o.ground;
            return this;
          },
        });
      }

      /* ------------------------------------------------------- public API */
      api = {
        /* ---- contract §5 ---- */

        /** A swept crescent card facing `dir`. See the header for opts. */
        slash(x, y, z, dx, dy, dz, o) {
          o = o || EMPTY;
          basisFrom(dx, dy, dz);
          const l = Math.hypot(dx, dy, dz) || 1;
          runLayers(DEFS.slash_arc, x, y, z, dx / l, dy / l, dz / l, o);
          return api;
        },

        /** The contact spark. `power` 0..2. */
        spark(x, y, z, dx, dy, dz, power = 1, o) {
          o = o || EMPTY;
          const kind = o.kind || 'slash';
          const def = DEFS['hit_' + kind] || DEFS.hit_slash;
          const fill = kind.endsWith('_fill');
          if (fill) telemetry.fill++; else telemetry.primary++;
          const l = Math.hypot(dx, dy, dz) || 1;
          const nx = dx / l, ny = dy / l, nz = dz / l;
          basisFrom(nx, ny, nz);
          _power.power = power; _power.scale = o.scale ?? 1;
          _power.element = o.element; _power.color = o.color; _power.color2 = o.color2;
          _power.roll = o.roll ?? 0; _power.alpha = o.alpha;
          _power.priority = o.priority ?? (fill ? 1 : 2);
          runLayers(def, x, y, z, nx, ny, nz, _power);
          if (o.element && o.element !== 'none' && DEFS['elem_' + o.element]) {
            _power.power = power * 0.6;
            runLayers(DEFS['elem_' + o.element], x, y, z, nx, ny, nz, _power);
          }
          return api;
        },

        /** Ground puff. opts.kind: 'step' | 'land' | 'slide'. */
        dust(x, y, z, power = 1, o) {
          o = o || EMPTY;
          const kind = o.kind || 'step';
          const def = DEFS['dust_' + kind] || DEFS.dust_step;
          const dx = o.dirX ?? 0, dz = o.dirZ ?? 0;
          const l = Math.hypot(dx, dz);
          const nx = l > 1e-4 ? dx / l : 0, ny = l > 1e-4 ? 0 : 1, nz = l > 1e-4 ? dz / l : 0;
          basisFrom(nx, ny, nz);
          _power.power = power; _power.scale = o.scale ?? 1;
          _power.element = o.element;
          _power.color = o.color ?? dustColorAt(x, z);
          _power.color2 = o.color2;
          _power.roll = 0; _power.alpha = o.alpha;
          _power.priority = o.priority ?? (kind === 'land' ? 1 : 0);
          runLayers(def, x, y, z, nx, ny, nz, _power);
          return api;
        },

        /** Terrain-conformed expanding shockwave. */
        ring(x, y, z, o) {
          o = o || EMPTY;
          beginEmissionFrame();
          const power = Math.max(0, o.power ?? 1);
          if (!gAdd || power <= 0) {
            telemetry.dropped++; frameEmission.dropped++;
            return api;
          }
          const el = elem(o.element || 'none');
          _col.setHex(o.color ?? el.light).convertSRGBToLinear();
          gAdd.stamp(W?.terrain, x, z,
            (o.radius ?? 4) * Math.sqrt(Math.min(2.25, power)), rnd() * 6.283, now, {
            life: o.life ?? 0.42, fadeIn: 0.02, mode: 1, thick: o.thick ?? 0.10,
            r: _col.r, g: _col.g, b: _col.b,
            a: (o.alpha ?? 1) * Math.min(1, power), lift: o.lift ?? 0.075,
          });
          telemetry.rings++;
          return api;
        },

        /** Omnidirectional blast. opts.kind: 'burst' | 'musou' | 'finish'. */
        burst(x, y, z, o) {
          o = o || EMPTY;
          const kind = o.kind || 'burst';
          const el = o.element || 'none';
          let def = DEFS.musou_burst;
          if (kind === 'finish') def = DEFS.musou_finish;
          else if (kind === 'burst') def = DEFS['elem_' + el] || DEFS.hit_crit;
          // The public filming helper may omit a direction. In that case use
          // the hero's authored facing so a finisher remains directional.
          const e = W?.entities, id = W?.playerId ?? -1;
          const yaw = id >= 0 && e?.alive[id] ? e.yaw[id] : 0;
          const dx = o.dirX ?? Math.sin(yaw), dy = o.dirY ?? 0.2, dz = o.dirZ ?? Math.cos(yaw);
          const dl = Math.hypot(dx, dy, dz) || 1;
          basisFrom(dx / dl, dy / dl, dz / dl);
          _power.power = o.power ?? 1; _power.scale = o.scale ?? 1;
          _power.element = el; _power.color = o.color; _power.color2 = o.color2;
          _power.roll = 0; _power.alpha = o.alpha;
          _power.priority = o.priority ?? ((kind === 'musou' || kind === 'finish') ? 2 : 1);
          telemetry.semantic++;
          if (kind === 'musou') telemetry.releases++;
          else if (kind === 'finish') { telemetry.finishes++; retireNearbyAuras(x, z); }
          runLayers(def, x, y, z, dx / dl, dy / dl, dz / dl, _power);
          return api;
        },

        /** Project a mark onto the terrain. Oldest-first recycling pool. */
        decal(x, y, z, o) {
          o = o || EMPTY;
          if (!gAlpha) return api;
          const cell = typeof o.kind === 'number' ? o.kind : (DECAL_CELL[o.kind] ?? DECAL_CELL.scuff);
          _col.setHex(o.color ?? 0x4a3d30).convertSRGBToLinear();
          gAlpha.stamp(W?.terrain, x, z, o.radius ?? 1, o.yaw ?? rnd() * 6.283, now, {
            life: o.life ?? 25, fadeIn: o.fadeIn ?? 0.004, mode: 0, cell,
            r: _col.r, g: _col.g, b: _col.b, a: o.alpha ?? 0.75, lift: o.lift ?? 0.045,
          });
          stats.decals++;
          return api;
        },

        /**
         * Acquire (or keep) a ribbon trail. Returns the handle; see the header
         * for the handle API. Passing an existing handle re-arms it.
         */
        trail(h) {
          if (h && typeof h === 'object' && typeof h.push === 'function') {
            if (!h.alive) return trails.acquire(h, now);
            h.on = true; h.age = 0; return h;
          }
          const o = h || EMPTY;
          const el = elem(o.element || 'none');
          const t = trails.acquire(EMPTY, now);
          if (!t) return null;
          t.configure({
            width: o.width ?? 1, intensity: o.intensity ?? 1,
            fadeFrames: o.fadeFrames ?? 10, taper: o.taper ?? 0.55,
            hot: o.hot || el.trailHot, tail: o.tail || el.trailTail,
            outset: o.outset ?? 0,
            obj: o.obj ?? null, tipY: o.tipY, rootY: o.rootY,
          });
          return t;
        },

        /* ---- extended ---- */

        /** Spawn any effect from the data table. */
        emit(name, x, y, z, o) {
          const def = DEFS[name];
          if (!def) return api;
          o = o || EMPTY;
          telemetry.semantic++;
          if (name === 'musouHit' || name === 'musou_hit') telemetry.musouHits++;
          else if (name === 'musouFinish' || name === 'musou_finish') {
            telemetry.finishes++;
            retireNearbyAuras(x, z);
          }
          else if (name === 'musouBurst' || name === 'musou_burst') telemetry.releases++;
          const dx = o.dirX ?? 0, dy = o.dirY ?? 1, dz = o.dirZ ?? 0;
          const l = Math.hypot(dx, dy, dz) || 1;
          basisFrom(dx / l, dy / l, dz / l);
          runLayers(def, x, y, z, dx / l, dy / l, dz / l, o);
          return api;
        },

        /** Full-body aura. `id` < 0 uses opts.x/y/z. Returns a handle. */
        aura(id, o) {
          o = o || EMPTY;
          const a = auraSlot();
          a.on = true;
          a.id = id ?? -1;
          a.x = o.x ?? 0; a.y = o.y ?? 0; a.z = o.z ?? 0;
          a.el = elem(o.element || 'none');
          a.intensity = o.intensity ?? 1;
          a.radius = o.radius ?? 0.55;
          a.height = o.height ?? 1.9;
          a.scale = o.scale ?? 1;
          a.priority = o.priority ?? 0;
          a.shafts = o.shafts !== false;
          a.ground = o.ground !== false;
          const seconds = Math.max(0.1, o.seconds ?? 3);
          a.until = now + seconds;
          a.fadeAt = a.until - Math.min(0.45, seconds * 0.30);
          a.acc = a.acc2 = 0; a.shaft = 0; a.pool = 0; a.lit = 0;
          return a;
        },

        /** A stretched quad between two world points. */
        beam(x0, y0, z0, x1, y1, z1, o) {
          o = o || EMPTY;
          const el = elem(o.element || 'none');
          resetB();
          B.x0 = x0; B.y0 = y0; B.z0 = z0; B.x1 = x1; B.y1 = y1; B.z1 = z1;
          B.life = o.life ?? 0.3;
          B.w0 = o.width ?? 0.25; B.w1 = o.width2 ?? B.w0;
          B.style = o.style ?? 0; B.jitter = o.jitter ?? 0.2;
          B.bright = o.bright ?? 2.4;
          const c0 = setCol0(o.color, el, 1), c1 = setCol0(o.color2, el, 2);
          B.r0 = c0[0]; B.g0 = c0[1]; B.b0 = c0[2]; B.a0 = o.alpha ?? 1;
          B.r1 = c1[0]; B.g1 = c1[1]; B.b1 = c1[2]; B.a1 = 0;
          const priority = o.priority ?? 1;
          if (grant(1, priority, true) > 0) {
            const before = beams.overwrites;
            pushed(beams, beams.push(now, priority), before, true);
          }
          return api;
        },

        footstep(x, y, z, power = 1) {
          _power.power = power; _power.scale = 1; _power.element = undefined;
          _power.color = dustColorAt(x, z); _power.color2 = undefined;
          _power.roll = 0; _power.alpha = undefined; _power.priority = 0;
          basisFrom(0, 1, 0);
          runLayers(DEFS.dust_step, x, y, z, 0, 1, 0, _power);
          return api;
        },

        land(x, y, z, power = 1) {
          _power.power = power; _power.scale = 0.7 + 0.5 * power;
          _power.element = undefined; _power.color = dustColorAt(x, z);
          _power.color2 = undefined; _power.roll = 0; _power.alpha = undefined; _power.priority = 1;
          basisFrom(0, 1, 0);
          runLayers(DEFS.dust_land, x, y, z, 0, 1, 0, _power);
          // Body landings call dust() and stay puff-only. This public hero
          // landing keeps one restrained authored ring instead of letting a
          // 20-body launcher stamp twenty overlapping ground swirls.
          _landRing.power = Math.min(1.2, power);
          api.ring(x, y, z, _landRing);
          W?.camrig?.shake?.(0.18 * power, 26, 0.16);
          return api;
        },

        slide(x, y, z, dx, dz, power = 1) {
          const l = Math.hypot(dx, dz) || 1;
          basisFrom(dx / l, 0, dz / l);
          _power.power = power; _power.scale = 1; _power.element = undefined;
          _power.color = dustColorAt(x, z); _power.color2 = undefined;
          _power.roll = 0; _power.alpha = undefined; _power.priority = 0;
          runLayers(DEFS.dust_slide, x, y, z, dx / l, 0.1, dz / l, _power);
          return api;
        },

        impactDust(x, y, z, power = 1) { return api.dust(x, y, z, power, DUST_LAND); },

        element(name) { return elem(name); },

        light(x, y, z, color = 0xffdca0, intensity = 8, radius = 10, life = 0.15) {
          claimLight(x, y, z, color, intensity, radius, life);
          return api;
        },

        haze(on = true) { hazeOn = !!on; return api; },

        /**
         * Switch the hero's blade ribbon on or off.
         *
         * Two ribbons are used, which is what separates a DW trail from a
         * flat streak: a weapon-specific near-white EDGE and a restrained
         * outer tint, both aggressively tapered and cleared in 6-8 frames.
         * They remain in the same shared draw
         * call as one.
         */
        weaponTrail(on = true, o) {
          o = o || EMPTY;
          if (!on) {
            if (heroTrail) { heroTrail.stop(); heroTrail = null; }
            if (heroGlow) { heroGlow.stop(); heroGlow = null; }
            return api;
          }
          syncHeroWeapon();
          if (!heroWeapon) return api;
          const b = heroWeapon.userData.__vfxBlade || BLADE.greatGlaive;
          const tipY = o.tipY ?? b[0], rootY = o.rootY ?? b[1];
          const el = elem(o.element || 'none');
          const inten = o.intensity ?? 1;
          const bladeWidth = (o.width ?? 1) * (b[2] ?? 0.34);
          if (!heroTrail || !heroTrail.alive) {
            heroGlow = api.trail({
              element: o.element || 'none', width: bladeWidth * 1.34,
              intensity: inten * 0.10, fadeFrames: (o.fadeFrames ?? 6) + 2,
              taper: 0.94, outset: 0.025,
              hot: el.core, tail: el.trailTail,
            });
            if (heroGlow) heroGlow.follow(heroWeapon, tipY, rootY);
            heroTrail = api.trail({
              element: o.element || 'none', width: bladeWidth,
              intensity: inten * 0.82, fadeFrames: o.fadeFrames ?? 6,
              taper: 0.86, outset: 0.008,
            });
            if (!heroTrail) return api;
            heroTrail.follow(heroWeapon, tipY, rootY);
          } else {
            heroTrail.configure({ intensity: inten * 0.82 });
            heroTrail.on = true; heroTrail.age = 0;
            if (heroGlow) { heroGlow.configure({ intensity: inten * 0.10 }); heroGlow.on = true; heroGlow.age = 0; }
          }
          heroTrailUntil = now + (o.hold ?? 0.20);
          return api;
        },

        clear() {
          padd.clear(); palpha.clear(); beams.clear();
          trails.clear(); gAlpha.clear(); gAdd.clear();
          for (const a of auras) a.on = false;
          for (const L of lights) { L.until = -1; L.light.intensity = 0; }
          sweeps.length = 0; heroTrail = null; heroGlow = null; heroWeapon = null;
          return api;
        },

        setQuality() {
          const q = CFG.q || {};
          const pMax = Math.max(2000, q.particleMax || 16000);
          density = Math.min(1.35, Math.max(0.15, pMax / 16000));
          budget.perFrame = Math.max(200, Math.round(pMax * 0.075));
          budget.reserve = Math.max(36, Math.round(budget.perFrame * 0.18));
          return api;
        },

        /**
         * Soft particles. `src/render/pipeline.js` renders the scene into an
         * HDR target that already carries a sampleable depth attachment but
         * does not publish it; the moment it does, hand it here and every
         * particle gets a depth fade against the geometry behind it.
         */
        setSceneDepth(tex, near = 0.25, far = 900) {
          for (const p of [padd, palpha]) {
            p.material.uniforms.uDepth.value = tex || null;
            p.material.uniforms.uSoft.value.set(tex ? 1.4 : 0, near, far);
            p.material.needsUpdate = true;
          }
          return api;
        },

        /* ---- introspection ---- */
        defs: DEFS,
        elements: ELEMENTS,
        sprites: SPR,
        decalKinds: DECAL_CELL,
        budget,
        stats,
        group,
        get time() { return now; },
        pools: { get add() { return padd; }, get alpha() { return palpha; },
                 get trails() { return trails; }, get beams() { return beams; },
                 get decals() { return gAlpha; }, get groundAdd() { return gAdd; } },

        /* ---- capture / QA only ---- */
        debug: {
          /**
           * Drive a synthetic blade sweep so the ribbon can be filmed without
           * combat being wired. Returns the trail handle.
           */
          sweep(o = {}) {
            const el = elem(o.element || 'none');
            const inten = o.intensity ?? 1;
            const bladeWidth = (o.width ?? 1) * (BLADE.greatGlaive[2] ?? 0.34);
            const glow = o.glow === false ? null : api.trail({
              element: o.element || 'none', width: bladeWidth * 1.34,
              intensity: inten * 0.10, fadeFrames: (o.fadeFrames ?? 6) + 2,
              taper: 0.94, outset: 0.025, hot: el.core, tail: el.trailTail,
            });
            const t = api.trail({
              element: o.element || 'none', width: bladeWidth,
              intensity: inten * 0.82, fadeFrames: o.fadeFrames ?? 6,
              taper: 0.86, outset: 0.008,
            });
            if (!t) return null;
            const tilt = o.tilt ?? 0.55;
            // arc plane: u is the "noon" direction, v the sweep direction
            const s = {
              t: 0, dur: o.dur ?? 0.34, trail: t, glow,
              cx: o.x ?? 0, cy: o.y ?? 1.35, cz: o.z ?? 0,
              rTip: o.reach ?? 2.1, rHilt: o.hilt ?? 0.55,
              a0: o.from ?? -1.5, a1: o.to ?? 1.5,
              ux: 0, uy: 1, uz: 0, vx: 1, vy: 0, vz: 0,
            };
            // tilt the plane: u leans away from vertical by `tilt` radians
            const yaw = o.yaw ?? 0;
            s.ux = Math.sin(tilt) * Math.sin(yaw); s.uy = Math.cos(tilt); s.uz = Math.sin(tilt) * Math.cos(yaw);
            s.vx = Math.cos(yaw); s.vy = 0; s.vz = -Math.sin(yaw);
            sweeps.push(s);
            return t;
          },
          /** Every def name, for a critic that wants to film all of them. */
          list() { return Object.keys(DEFS); },
          /** Toggle the whole VFX group so a capture can diff draw calls. */
          visible(on) { group.visible = on !== false; return group.visible; },
          counts() {
            let drawn = 0;
            for (const m of group.children) if (m.visible) drawn++;
            return {
              add: padd.count(now), alpha: palpha.count(now),
              trails: trails.active, beams: beams.count(now), beamInstances: beams.geometry.instanceCount,
              addSlots: padd.max, alphaSlots: palpha.max,
              decalSlots: gAlpha.slots, meshes: group.children.length,
              drawn,
              visible: group.children.map(m => m.name + ':' + (m.visible ? 1 : 0)),
            };
          },
          telemetry() { return telemetry; },
          resetTelemetry() { resetTelemetry(); return telemetry; },
          setTime(t) { now = t; return now; },
        },
      };

      w.vfx = api;

      /* ------------------------------------------------------ bus + harness */
      w.bus?.on?.('anim:event', onAnimEvent);
      if (w.vfxAutoHit) w.bus?.on?.('hit', onHit);   // impact.js already routes hits
      w.bus?.on?.('quality', () => api.setQuality());

      const dw = typeof window !== 'undefined' ? window.__DW : null;
      if (dw && dw.scenarios) {
        dw.scenarios.vfx = (wd, name, x, y, z, o) => {
          const e = wd.entities, id = wd.playerId;
          const px = x ?? (id >= 0 ? e.x[id] : 0);
          const pz = z ?? (id >= 0 ? e.z[id] : 0);
          const py = y ?? (wd.terrain ? wd.terrain.heightAt(px, pz) + 1.2 : 1.2);
          wd.vfx.emit(name, px, py, pz, o || undefined);
          return name;
        };
        dw.scenarios.vfxSpark = (wd, kind = 'slash', x, y, z, power = 1, el) => {
          const e = wd.entities, id = wd.playerId;
          const px = x ?? (id >= 0 ? e.x[id] : 0), pz = z ?? (id >= 0 ? e.z[id] : 0);
          const py = y ?? (wd.terrain ? wd.terrain.heightAt(px, pz) + 1.25 : 1.25);
          wd.vfx.spark(px, py, pz, 0.2, 0.35, 1, power, { kind, element: el });
          return kind;
        };
        dw.scenarios.vfxSweep = (wd, o) => { wd.vfx.debug.sweep(o || {}); return true; };
        dw.scenarios.vfxAura = (wd, o) => { wd.vfx.aura(-1, o || {}); return true; };
        dw.scenarios.vfxVisible = (wd, on) => wd.vfx.debug.visible(on);
        dw.scenarios.vfxCounts = (wd) => wd.vfx.debug.counts();
        dw.scenarios.vfxClear = (wd) => { wd.vfx.clear(); return true; };
        dw.scenarios.vfxHaze = (wd, on) => { wd.vfx.haze(on); return !!on; };
      }
    },

    /**
     * Everything runs on realDt: a hit spark must keep expanding while the
     * simulation is frozen for hitstop — that freeze is precisely when the
     * player is looking at it.
     */
    lateUpdate(w, dt, alpha, realDt) {
      const rt = realDt || dt || 1 / 60;
      now += rt;
      crowdSlashBudget = 6;
      beginEmissionFrame();

      syncHeroWeapon();
      driveHeroTrail(rt);
      stepSweeps(rt);
      for (let i = 0; i < auras.length; i++) stepAura(auras[i], rt);
      stepHaze(rt);
      updateLights();

      trails.update(now, rt);
      padd.flush(now);
      palpha.flush(now);
      beams.flush(now);
      gAlpha.flush(now);
      gAdd.flush(now);

      stats.addInstances = padd.geometry.instanceCount;
      stats.alphaInstances = palpha.geometry.instanceCount;
      stats.addLive = padd.count(now);
      stats.alphaLive = palpha.count(now);
      stats.particles = stats.addLive + stats.alphaLive;
      stats.trails = trails.active;
      stats.beams = beams.count(now);
      stats.beamInstances = beams.geometry.instanceCount;
      stats.spawnedThisFrame = frameEmission.spawned;
      let drawn = 0;
      for (let i = 0; i < group.children.length; i++) if (group.children[i].visible) drawn++;
      stats.drawCalls = drawn;
      stats.auras = auras.reduce(countOn, 0);
      stats.haze = hazeCount;
    },

    resize(wpx, hpx, dpr) {
      const W2 = Math.max(2, Math.round(wpx * dpr)), H2 = Math.max(2, Math.round(hpx * dpr));
      padd?.material.uniforms.uRes.value.set(W2, H2);
      palpha?.material.uniforms.uRes.value.set(W2, H2);
    },

    dispose() {
      padd?.dispose(); palpha?.dispose(); trails?.dispose(); beams?.dispose();
      gAlpha?.dispose(); gAdd?.dispose();
      atlasTex?.dispose(); trailTex?.dispose(); decalTex?.dispose();
      group?.parent?.remove(group);
    },
  };
}

const EMPTY = Object.freeze({});
const DUST_LAND = Object.freeze({ kind: 'land' });
const _landRing = { radius: 1.9, life: 0.28, thick: 0.08, alpha: 0.22,
  color: 0xd8c9ac, power: 1, priority: 1 };
/** Reusable opts record so the public calls never allocate. */
const _power = { power: 1, scale: 1, element: undefined, color: undefined,
  color2: undefined, roll: 0, alpha: undefined, priority: 1 };
const countOn = (n, a) => n + (a.on ? 1 : 0);
